import { createRouter, createWebHistory } from 'vue-router'

import administracao from './administracao.js'
import almoxarifado from './almoxarifado/index.js'
import configuracao from './configuracao.js'
import saneamento from './saneamento/index.js'
import frotas from './frotas/index.js'
import obra from './obra/index.js'
import recursosHumanos from './recursosHumanos/index.js'
import relatorios from './relatorio/index.js'
import sistema from './sistema.js'

export const routes = [
  ...administracao,
  ...almoxarifado,
  ...configuracao,
  ...frotas,
  ...obra,
  ...recursosHumanos,
  ...relatorios,
  ...saneamento,
  ...sistema,
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/Home.vue'),
  },
  {
    path: '/',
    name: 'base',
    component: () => import('@/views/Home.vue'),
  },
  {
    path: '/meu_perfil',
    name: 'myProfile',
    component: () => import('@/views/Perfil.vue'),
    meta: { breadcrumb: [{ title: 'Meu Perfil' }] },
  },
  {
    path: '/acesso-restrito',
    name: 'acessoRestrito',
    component: () => import('@/views/AcessoRestrito.vue'),
  },
]

const normalizePath = (path) => {
  const raw = String(path || '')
    .toLowerCase()
    .trim()
  if (!raw) return '/'

  const onlyPath = raw.split('?')[0].split('#')[0]
  const normalized = onlyPath.replace(/^\/+/g, '').replace(/\/+$/g, '')
  return normalized ? `/${normalized}` : '/'
}

const routeTemplateToRegex = (routeTemplate) => {
  const normalizedTemplate = normalizePath(routeTemplate)
  const escaped = normalizedTemplate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const withParams = escaped.replace(/:([a-zA-Z0-9_]+)/g, '[^/]+')
  return new RegExp(`^${withParams}$`, 'i')
}

const matchesRoute = (path, routeTemplate) => {
  if (!routeTemplate) {
    return false
  }
  const regex = routeTemplateToRegex(routeTemplate)
  return regex.test(path)
}

const matchesRouteOrChild = (path, routeTemplate) => {
  const normalizedPath = normalizePath(path)
  const normalizedTemplate = normalizePath(routeTemplate)
  if (!normalizedPath || !normalizedTemplate) {
    return false
  }

  if (matchesRoute(normalizedPath, normalizedTemplate)) {
    return true
  }

  const escaped = normalizedTemplate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const withParams = escaped.replace(/:([a-zA-Z0-9_]+)/g, '[^/]+')
  const childRegex = new RegExp(`^${withParams}(?:/.*)?$`, 'i')
  return childRegex.test(normalizedPath)
}

const getStoredUser = () => {
  const rawUser = localStorage.getItem('user')
  if (!rawUser || rawUser === 'undefined' || rawUser === 'null') {
    return null
  }

  try {
    return JSON.parse(rawUser)
  } catch (error) {
    return null
  }
}

const canAccessRoute = (toPath, user) => {
  const alwaysAllowed = new Set(['/home', '/', '/meu_perfil', '/acesso-restrito'])
  const path = normalizePath(toPath)

  if (alwaysAllowed.has(path)) {
    return true
  }

  const permissions = user?.permissions?.byScreenRoute || {}
  const entries = Object.entries(permissions)
  if (!entries.length) {
    return false
  }

  return entries.some(([screenRoute, permission]) => {
    if (!permission?.get) {
      return false
    }

    return matchesRouteOrChild(path, screenRoute)
  })
}

const hasScopePermission = (user, permissionKey) => {
  const raw = String(permissionKey || '')
    .toLowerCase()
    .trim()
  if (!raw) {
    return true
  }

  const lastDot = raw.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === raw.length - 1) {
    return false
  }

  const scope = raw.slice(0, lastDot)
  const action = raw.slice(lastDot + 1)
  const resourcePermissions = user?.permissions?.byResource || {}
  const scopePermissions = user?.permissions?.byScope || {}

  if (resourcePermissions[scope]?.[action] !== undefined) {
    return !!resourcePermissions[scope]?.[action]
  }

  return !!scopePermissions[scope]?.[action]
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

router.beforeEach((to) => {
  const targetPath = normalizePath(to.path)
  const user = getStoredUser()

  if (targetPath === '/login') {
    return true
  }

  if (!user?.id) {
    return { path: '/login' }
  }

  if (!canAccessRoute(to.path, user)) {
    return { path: '/acesso-restrito' }
  }

  if (to.meta?.requiredPermission && !hasScopePermission(user, to.meta.requiredPermission)) {
    return { path: '/acesso-restrito' }
  }

  return true
})

export default router
